import { Box, Typography } from '@mui/material';
import { MessageCircle, MessageSquarePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Loader } from '@/modules/common/components';

import { useConversation } from '../application/use-conversation';
import { SendMessageConversation } from '../types/conversation';
import { ChatCard } from './ChatCard';

export const ChatConversations = ({
  onSelectConversation,
  onSelectChatWindow,
}: {
  onSelectConversation: (conversation: SendMessageConversation | null) => void;
  onSelectChatWindow: () => void;
}) => {
  const { t } = useTranslation();
  const { conversations, conversationsLoading } = useConversation();

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box p={2} borderBottom={1} borderColor="divider">
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography py={0.5}>{t('modules.chat.chatConversations.title')}</Typography>
        </Box>
      </Box>
      <Box flex={1} overflow="auto" p={1.5} bgcolor="white">
        {conversationsLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Loader />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" height="100%" position="relative">
            <Box flex={1} overflow="hidden">
              <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                overflow="auto"
                pb={10}
                sx={{
                  maskImage:
                    'linear-gradient(to bottom, black calc(100% - 64px), transparent 100%)',
                  WebkitMaskImage:
                    'linear-gradient(to bottom, black calc(100% - 64px), transparent 100%)',
                }}
              >
                {!conversations || conversations.length === 0 ? (
                  <Box
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    p={4}
                    textAlign="center"
                  >
                    <Box
                      width={48}
                      height={48}
                      bgcolor="primary.50"
                      borderRadius="50%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mb={1.5}
                    >
                      <MessageCircle className="w-6 h-6 text-indigo-600" />
                    </Box>
                    <Typography variant="h6" color="text.primary" mb={0.5}>
                      {t('modules.chat.chatConversations.noConversations.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" maxWidth={320}>
                      {t('modules.chat.chatConversations.noConversations.description')}
                    </Typography>
                  </Box>
                ) : (
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    {conversations.map((conversation) => (
                      <ChatCard
                        key={conversation.id}
                        title={conversation.title || conversation.lastMessage?.content || ''}
                        date={conversation.lastMessage?.createdAt || ''}
                        status={conversation.lastMessage?.status || null}
                        labels={conversation.status}
                        onClick={() => {
                          onSelectConversation(conversation);
                          onSelectChatWindow();
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
            <div className="absolute bottom-4 left-0 right-0 flex justify-center">
              <Button
                className="shadow-lg flex items-center gap-2 px-6"
                onClick={onSelectChatWindow}
              >
                <MessageSquarePlus className="w-5 h-5" />
                {t('modules.chat.chatConversations.askQuestion')}
              </Button>
            </div>
          </Box>
        )}
      </Box>
    </Box>
  );
};
